import type { SalesforceProduct } from '~/lib/salesforce/types';
import type { TranslateFn } from '~/shared/utils/translation/hooks/useTranslation';
import type { ReservationStoreBase } from '~/features/product-details/components/ReservationDrawer/types';
import { isFreightByCarrier } from '~/lib/salesforce/utilities/shipping';

// Quantity defaults to 1, to check if the store allows reservation or delivery at all
export const canReserveFromStore = (store: ReservationStoreBase, quantity = 1): boolean =>
    Boolean(store.c_clickReserveEnabled) && quantity <= (store.c_stock_level_clickreserve ?? 0);

// Store does click and collect for this product.
export const isDeliveryOffered = (
    store: ReservationStoreBase,
    product?: SalesforceProduct | null,
): boolean =>
    Boolean(store.c_clickCollectEnabled) && !isFreightByCarrier(product?.c_itemFreightGroup);

export const canDeliverFromStore = (
    store: ReservationStoreBase,
    product?: SalesforceProduct | null,
    quantity = 1,
): boolean => isDeliveryOffered(store, product) && quantity <= (product?.inventory?.ats ?? 0);

type StoreAvailabilityCounts = {
    availableStoresCr: number;
    availableStoresCc: number;
    totalStores: number;
};

export const countStoreAvailability = (
    stores: ReservationStoreBase[],
    product?: SalesforceProduct | null,
    quantity?: number,
): StoreAvailabilityCounts => {
    let availableStoresCr = 0;
    let availableStoresCc = 0;

    for (const store of stores) {
        if (canReserveFromStore(store, quantity)) availableStoresCr += 1;
        if (canDeliverFromStore(store, product, quantity)) availableStoresCc += 1;
    }

    return { availableStoresCr, availableStoresCc, totalStores: stores.length };
};

export type StoreReservationStatus = {
    text: string;
    tone: 'green' | 'yellow' | 'red';
    showCta: boolean;
    buttonText: string;
    opensMinibasket: boolean;
};

export const getStoreReservationStatus = ({
    store,
    product,
    quantity,
    translate,
}: {
    store: ReservationStoreBase;
    product?: SalesforceProduct | null;
    quantity: number;
    translate: TranslateFn;
}): StoreReservationStatus => {
    const reserve = {
        text: translate('pdp.pickupInOneHour'),
        tone: 'green' as const,
        buttonText: translate('pdp.reserve'),
        opensMinibasket: false,
    };

    const deliver = {
        text: translate('pdp.shipToStoreInDays', {
            deliveryDays: product?.c_deliveryDaysRange || '',
        }),
        tone: 'yellow' as const,
        buttonText: translate('pdp.buyAndShipToStore'),
        opensMinibasket: true,
    };

    if (canReserveFromStore(store, quantity)) return { ...reserve, showCta: true };

    // Too many to Click and Reserve, so fall back to Click and Collect instead
    if (canDeliverFromStore(store, product, quantity)) return { ...deliver, showCta: true };

    // The store has click and reserve or click and collect enabled but the customer has exceeded
    // the amount available in the quantity selector
    if (canReserveFromStore(store) || isDeliveryOffered(store, product)) {
        return {
            ...reserve,
            text: translate('pdp.cannotDeliverSelectedQuantity'),
            tone: 'red',
            showCta: false,
        };
    }

    return {
        text: translate('pdp.storeTemporarilyClosedPickup'),
        tone: 'red',
        showCta: false,
        buttonText: translate('pdp.reserve'),
        opensMinibasket: false,
    };
};
